import React, { createContext, FC, PropsWithChildren, useEffect, useMemo } from "react";

import { cloneDeep } from "lodash";
import { useSessionStorage } from "react-use";

import {
    existSomePhysicalPolicyholder,
    getMoralPolicyholders,
    getPhysicalPolicyholders,
} from "@/components/features/User/utils";
import { MAX_POLICYHOLDERS } from "@/components/organisms/PolicyholderDropdown/utils";
import mobileIntegration from "@/integration";
import { PolicyHolder, UserState } from "@/shared/domain";
import { useAuth, usePolicyholderApi } from "@/shared/hooks";

export const GLOBAL_FILTER_STORAGE_KEY = "globalFilter";

type GlobalFilterStorage = {
    policyholderIds?: number[];
    isProEnvironment?: boolean;
};

export const globalFilterStorageDefault: GlobalFilterStorage = {
    policyholderIds: [],
    isProEnvironment: null,
};

interface IGlobalFilterContext {
    policyholderGF: number[];
    isProEnvironment?: boolean;
    updatePolicyholderGF?: (value: number[]) => void;
    updateIsProEnvironment?: (value: boolean) => void;
    removeGF?: () => void;
}

export const GlobalFilterContext = createContext<IGlobalFilterContext>({ policyholderGF: [], isProEnvironment: null });
GlobalFilterContext.displayName = "GlobalFilterContext";

export const GlobalFilterProvider: FC<PropsWithChildren> = ({ children }) => {
    const { user, userState } = useAuth();
    const [filtersGFStored, setFiltersGFStored] = useSessionStorage<GlobalFilterStorage>(
        GLOBAL_FILTER_STORAGE_KEY,
        globalFilterStorageDefault,
    );

    const { isProEnvironment, policyholderIds } = filtersGFStored;

    const { getAllPolicyholders } = usePolicyholderApi(isProEnvironment, user, userState);

    const removeGF = () => setFiltersGFStored(globalFilterStorageDefault);

    useEffect(() => {
        if (userState === UserState.UNKNOWN) {
            removeGF();
        }
    }, [userState]);

    const updatePolicyholderGF = (value: number[]) => {
        // @ts-ignore - type update needed on react-use lib
        setFiltersGFStored((previousValue) => ({ ...previousValue, policyholderIds: value }));
    };

    const updateIsProEnvironmentAndPolicyholders = (
        isProEnvironmentNewData: boolean,
        allPolicyholders: PolicyHolder[] = [],
    ) => {
        if (!mobileIntegration.isNativeApp()) {
            // @ts-ignore - type update needed on react-use lib
            setFiltersGFStored((previousValue) => ({ ...previousValue, isProEnvironment: isProEnvironmentNewData }));
        }

        const physicalPolicyholders = getPhysicalPolicyholders(allPolicyholders);
        const moralPolicyholders = getMoralPolicyholders(allPolicyholders);

        const isUserPhysical = physicalPolicyholders.some(
            (policyholder) => user.policyHolderId === policyholder.policyHolderId,
        );

        if (!isProEnvironmentNewData) {
            if (physicalPolicyholders.length > MAX_POLICYHOLDERS) {
                const physicalPolicyholdersReversed = cloneDeep(physicalPolicyholders).reverse();
                const firstOrMainPolicyholderId = isUserPhysical
                    ? user.policyHolderId
                    : physicalPolicyholdersReversed[0].policyHolderId;
                updatePolicyholderGF([firstOrMainPolicyholderId]);
            } else {
                updatePolicyholderGF(physicalPolicyholders.map((policyholder) => policyholder.policyHolderId));
            }
        } else {
            const moralPolicyholdersReversed = cloneDeep(moralPolicyholders).reverse();
            const firstOrMainPolicyholderId = !isUserPhysical
                ? user.policyHolderId
                : moralPolicyholdersReversed[0].policyHolderId;
            updatePolicyholderGF([firstOrMainPolicyholderId]);
        }
    };

    const policyholders = getAllPolicyholders();
    if (isProEnvironment === null && !mobileIntegration.isNativeApp()) {
        if (policyholders.length) {
            const existPhysical = existSomePhysicalPolicyholder(policyholders);
            if (existPhysical) {
                updateIsProEnvironmentAndPolicyholders(false, policyholders);
            } else {
                updateIsProEnvironmentAndPolicyholders(true, policyholders);
            }
        }
    }

    const updateIsProEnvironment = (value: boolean) => {
        updateIsProEnvironmentAndPolicyholders(value, policyholders);
    };

    const contextValue = useMemo(
        (): IGlobalFilterContext => ({
            policyholderGF: policyholderIds,
            updatePolicyholderGF,
            isProEnvironment,
            updateIsProEnvironment,
            removeGF,
        }),
        [policyholderIds, isProEnvironment],
    );

    return <GlobalFilterContext.Provider value={contextValue}>{children}</GlobalFilterContext.Provider>;
};
